import type { CloudProvider, ProviderMode } from '../stores/uploadSessionStore';

export const BYOK_FILE_SIZE_LIMIT = 25 * 1024 * 1024;
const TARGET_SAMPLE_RATE = 16000;
const TARGET_CHANNELS = 1;
export const GROQ_CHUNK_LENGTH_SEC = 600;
export const GROQ_CHUNK_OVERLAP_SEC = 10;
const BYOK_CLOUD_PROVIDERS = new Set<CloudProvider>(['openai', 'groq', 'mistral']);

function replaceExtension(fileName: string, nextExtension: string) {
  const baseName = fileName.replace(/\.[^.]+$/, '');
  return `${baseName}.${nextExtension}`;
}

function buildChunkFileName(fileName: string, chunkNumber: number) {
  const baseName = fileName.replace(/\.[^.]+$/, '');
  return `${baseName}.part-${String(chunkNumber).padStart(2, '0')}.wav`;
}

function audioBufferToWav(buffer: AudioBuffer) {
  const length = buffer.length;
  const arrayBuffer = new ArrayBuffer(44 + length * 2);
  const view = new DataView(arrayBuffer);
  const channelData = buffer.getChannelData(0);

  const writeString = (offset: number, value: string) => {
    for (let index = 0; index < value.length; index += 1) {
      view.setUint8(offset + index, value.charCodeAt(index));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, TARGET_CHANNELS, true);
  view.setUint32(24, buffer.sampleRate, true);
  view.setUint32(28, buffer.sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, length * 2, true);

  // Int16Array shares the same ArrayBuffer at offset 44 — always little-endian on x86/ARM,
  // which matches WAV's required byte order. Avoids per-sample DataView overhead and allows
  // the JIT to emit vectorised (SIMD) instructions: ~3-5× faster than setInt16 in a loop.
  const pcm = new Int16Array(arrayBuffer, 44);
  for (let index = 0; index < length; index += 1) {
    const sample = Math.max(-1, Math.min(1, channelData[index]));
    pcm[index] = (sample < 0 ? sample * 0x8000 : sample * 0x7fff) | 0;
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

async function renderFileToMonoBuffer(file: File) {
  const AudioContextCtor =
    window.AudioContext ||
    (
      window as Window &
        typeof globalThis & {
          webkitAudioContext?: typeof AudioContext;
        }
    ).webkitAudioContext;
  if (!AudioContextCtor) {
    throw new Error('This browser cannot optimize audio files for cloud transcription.');
  }

  const audioContext = new AudioContextCtor();

  try {
    const arrayBuffer = await file.arrayBuffer();
    const decodedAudio = await audioContext.decodeAudioData(arrayBuffer.slice(0));
    const frameCount = Math.ceil(decodedAudio.duration * TARGET_SAMPLE_RATE);
    const offlineContext = new OfflineAudioContext(
      TARGET_CHANNELS,
      frameCount,
      TARGET_SAMPLE_RATE,
    );

    const source = offlineContext.createBufferSource();
    source.buffer = decodedAudio;
    source.connect(offlineContext.destination);
    source.start();

    return await offlineContext.startRendering();
  } finally {
    await audioContext.close().catch(() => undefined);
  }
}

function createMonoChunkBuffer(channelData: Float32Array, startFrame: number, endFrame: number) {
  const frameLength = Math.max(1, endFrame - startFrame);
  // new AudioBuffer() avoids spinning up a throwaway OfflineAudioContext.
  // subarray() gives a zero-copy view instead of copying up to ~38 MB per chunk.
  const chunkBuffer = new AudioBuffer({
    numberOfChannels: TARGET_CHANNELS,
    length: frameLength,
    sampleRate: TARGET_SAMPLE_RATE,
  });
  chunkBuffer.copyToChannel(channelData.subarray(startFrame, endFrame) as Float32Array<ArrayBuffer>, 0);
  return chunkBuffer;
}

export type ChunkedAudioFile = {
  file: File;
  startSec: number;
  endSec: number;
  index: number;
  total: number;
};

export type ChunkBoundary = {
  startSec: number;
  endSec: number;
  index: number;
  total: number;
};

export function buildChunkBoundaries(totalDurationSec: number): ChunkBoundary[] {
  const stepSec = Math.max(1, GROQ_CHUNK_LENGTH_SEC - GROQ_CHUNK_OVERLAP_SEC);
  const boundaries: Array<{ startSec: number; endSec: number }> = [];

  for (let startSec = 0; startSec < totalDurationSec; startSec += stepSec) {
    boundaries.push({
      startSec,
      endSec: Math.min(totalDurationSec, startSec + GROQ_CHUNK_LENGTH_SEC),
    });
  }

  return boundaries.map(({ startSec, endSec }, index) => ({
    startSec,
    endSec,
    index: index + 1,
    total: boundaries.length,
  }));
}

export function buildChunkFromBuffer(
  renderedBuffer: AudioBuffer,
  originalName: string,
  originalLastModified: number,
  boundary: ChunkBoundary,
): ChunkedAudioFile {
  const channelData = renderedBuffer.getChannelData(0);
  const startFrame = Math.floor(boundary.startSec * TARGET_SAMPLE_RATE);
  const endFrame = Math.ceil(boundary.endSec * TARGET_SAMPLE_RATE);
  const chunkBuffer = createMonoChunkBuffer(channelData, startFrame, endFrame);
  const wavBlob = audioBufferToWav(chunkBuffer);

  return {
    file: new File([wavBlob], buildChunkFileName(originalName, boundary.index), {
      type: 'audio/wav',
      lastModified: originalLastModified,
    }),
    startSec: boundary.startSec,
    endSec: boundary.endSec,
    index: boundary.index,
    total: boundary.total,
  };
}

export type PreparedTranscriptionFile = {
  file: File;
  optimized: boolean;
  stillOverLimit: boolean;
  /** Pre-decoded AudioBuffer — only kept when Groq chunking will need it. */
  renderedBuffer?: AudioBuffer;
};

export async function prepareFileForTranscription(
  file: File,
  mode: ProviderMode,
  cloudProvider: CloudProvider,
): Promise<PreparedTranscriptionFile> {
  if (mode !== 'cloud' || !BYOK_CLOUD_PROVIDERS.has(cloudProvider)) {
    return { file, optimized: false, stillOverLimit: false };
  }

  if (file.size <= BYOK_FILE_SIZE_LIMIT) {
    return { file, optimized: false, stillOverLimit: false };
  }

  let renderedBuffer: AudioBuffer;
  let optimizedFile: File;
  try {
    renderedBuffer = await renderFileToMonoBuffer(file);
    const wavBlob = audioBufferToWav(renderedBuffer);
    optimizedFile = new File([wavBlob], replaceExtension(file.name, 'wav'), {
      type: 'audio/wav',
      lastModified: file.lastModified,
    });
  } catch (error) {
    throw new Error(
      error instanceof Error
        ? `Browser-side audio optimization failed: ${error.message}`
        : 'Browser-side audio optimization failed.',
    );
  }

  const stillOverLimit = optimizedFile.size > BYOK_FILE_SIZE_LIMIT;
  if (stillOverLimit && cloudProvider !== 'groq') {
    throw new Error(
      'This file is still over 25 MB after browser-side optimization. Switch to local mode, OpenWhispr Cloud, or split the audio first.',
    );
  }

  return {
    file: optimizedFile,
    optimized: true,
    stillOverLimit,
    renderedBuffer: cloudProvider === 'groq' && stillOverLimit ? renderedBuffer : undefined,
  };
}
